import { Alerta, Badge, Button, DataList, Dialog, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { Miembro, Rol } from "@/lib/api";
import { ETIQUETA_ESTADO, etiquetaRolMiembro, OPCIONES_ROL, sinVincular, TONO_ESTADO } from "@/lib/usuarios";
import { ElegirFicha, PasswordTemporalParaCompartir } from "./PasswordTemporal";
import { useUsuariosTab } from "./useUsuariosTab";

export function UsuariosTab() {
  const t = useUsuariosTab();
  const d = t.dialogo;

  const columnas = [
    { clave: "nombre", titulo: "Nombre", render: (m: Miembro) => (t.esYo(m) ? `${m.nombre} (vos)` : m.nombre) },
    { clave: "email", titulo: "Email", render: (m: Miembro) => <span className="break-all">{m.email}</span> },
    {
      clave: "rol",
      titulo: "Rol",
      render: (m: Miembro) =>
        sinVincular(m) ? (
          <Row espacio={2}>
            <span>{etiquetaRolMiembro(m)}</span>
            <Badge tono="alerta">Sin vincular</Badge>
          </Row>
        ) : (
          etiquetaRolMiembro(m)
        ),
    },
    { clave: "estado", titulo: "Estado", render: (m: Miembro) => <Badge tono={TONO_ESTADO[m.estado]}>{ETIQUETA_ESTADO[m.estado]}</Badge> },
  ];

  const acciones = (m: Miembro) => (
    <>
      {!t.esYo(m) && (
        <Button variante="secundario" tamanio="sm" aria-label={`Cambiar rol de ${m.nombre}`} onClick={() => t.abrirRol(m)}>
          Rol
        </Button>
      )}
      {sinVincular(m) && (
        <Button variante="secundario" tamanio="sm" aria-label={`Vincular a ${m.nombre} con su ficha`} onClick={() => t.abrirVincular(m)}>
          Vincular
        </Button>
      )}
      {m.activo && !t.esYo(m) && (
        <Button variante="secundario" tamanio="sm" aria-label={`Generar contraseña nueva para ${m.nombre}`} onClick={() => t.abrirTemporal(m)}>
          Contraseña
        </Button>
      )}
      {!t.esYo(m) && (
        <Button
          variante="fantasma"
          tamanio="sm"
          aria-label={`${m.activo ? "Desactivar a" : "Reactivar a"} ${m.nombre}`}
          onClick={() => t.abrirActivo(m)}
        >
          {m.activo ? "Desactivar" : "Reactivar"}
        </Button>
      )}
    </>
  );

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Usuarios</h1>
        <Button onClick={t.abrirAlta}>Nuevo usuario</Button>
      </Row>
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList etiqueta="Usuarios" columnas={columnas} filas={t.miembros} claveFila={(m) => m.id} acciones={acciones} vacio="Todavía no hay usuarios." />
      )}

      <Dialog abierto={d?.tipo === "alta"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Nuevo usuario">
        {t.resultadoAlta ? (
          <Stack espacio={4}>
            {t.resultadoAlta.temporal ? (
              <PasswordTemporalParaCompartir datos={t.resultadoAlta.temporal} />
            ) : (
              <p>{t.resultadoAlta.nombre} ya tenía usuario: puede entrar con su contraseña de siempre.</p>
            )}
            <Button onClick={t.cerrar}>Listo</Button>
          </Stack>
        ) : (
          <form onSubmit={(e) => void t.crear(e)} noValidate>
            <Stack espacio={4}>
              <Select etiqueta="Rol" opciones={OPCIONES_ROL} valor={t.alta.rol || undefined} onCambio={(v) => t.cambiarAlta("rol", v)} />
              {t.alta.rol === "fiduciante" && (
                <ElegirFicha
                  opciones={t.opcionesFichas}
                  elegida={t.nombreFicha(t.alta.fichaId)}
                  onElegir={t.elegirFichaAlta}
                  onSoltar={t.soltarFichaAlta}
                  sinFichasLibres={t.sinFichasLibres}
                  error={t.errorFichas}
                />
              )}
              <TextField etiqueta="Nombre" value={t.alta.nombre} onChange={(e) => t.cambiarAlta("nombre", e.target.value)} />
              <TextField etiqueta="Email" type="email" inputMode="email" value={t.alta.email} onChange={(e) => t.cambiarAlta("email", e.target.value)} />
              <Alerta texto={t.errorAlta} />
              <Row justificar="fin" espacio={2}>
                <Button variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.creando} disabled={!t.puedeCrear}>
                  Crear usuario
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "rol"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Cambiar rol">
        {d?.tipo === "rol" && (
          <Stack espacio={4}>
            <p>{d.miembro.nombre}</p>
            <Select etiqueta="Rol" opciones={OPCIONES_ROL} valor={d.rol} onCambio={(v) => t.elegirRol(v as Rol)} />
            {t.rolPideFicha && (
              <ElegirFicha
                opciones={t.opcionesFichas}
                elegida={t.nombreFicha(d.fichaId)}
                onElegir={t.elegirFichaDialogo}
                onSoltar={t.soltarFichaDialogo}
                sinFichasLibres={t.sinFichasLibres}
                error={t.errorFichas}
              />
            )}
            <Alerta texto={t.errorEditar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button cargando={t.guardando} disabled={t.rolPideFicha && d.fichaId === null} onClick={() => void t.guardarRol()}>
                Guardar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "vincular"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Vincular con su ficha">
        {d?.tipo === "vincular" && (
          <Stack espacio={4}>
            <p>{d.miembro.nombre} tiene el rol fiduciante pero no está vinculado a ninguna ficha: elegí cuál es la suya.</p>
            <ElegirFicha
              opciones={t.opcionesFichas}
              elegida={t.nombreFicha(d.fichaId)}
              onElegir={t.elegirFichaDialogo}
              onSoltar={t.soltarFichaDialogo}
              sinFichasLibres={t.sinFichasLibres}
              error={t.errorFichas}
            />
            <Alerta texto={t.errorEditar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button cargando={t.guardando} disabled={d.fichaId === null} onClick={() => void t.guardarVincular()}>
                Vincular
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "confirmar-temporal"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Contraseña nueva">
        {d?.tipo === "confirmar-temporal" && (
          <Stack espacio={4}>
            <p>
              ¿Generar una contraseña temporal nueva para {d.miembro.nombre}? La anterior deja de servir y se le cierran las sesiones abiertas.
            </p>
            <Alerta texto={t.errorTemporal} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button cargando={t.generando} onClick={() => void t.generarTemporal()}>
                Generar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "temporal"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Contraseña temporal">
        {d?.tipo === "temporal" && (
          <Stack espacio={4}>
            <PasswordTemporalParaCompartir datos={d.datos} />
            <Button onClick={t.cerrar}>Listo</Button>
          </Stack>
        )}
      </Dialog>

      <Dialog
        abierto={d?.tipo === "activo"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo={d?.tipo === "activo" && !d.miembro.activo ? "Reactivar usuario" : "Desactivar usuario"}
      >
        {d?.tipo === "activo" && (
          <Stack espacio={4}>
            <p>
              {d.miembro.activo
                ? `¿Desactivar a ${d.miembro.nombre}? Se le cierran las sesiones y no va a poder entrar hasta que lo reactives.`
                : `¿Reactivar a ${d.miembro.nombre}? Va a poder entrar de nuevo con su contraseña.`}
            </p>
            <Alerta texto={t.errorEditar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante={d.miembro.activo ? "peligro" : "primario"} cargando={t.guardando} onClick={() => void t.confirmarActivo()}>
                {d.miembro.activo ? "Desactivar" : "Reactivar"}
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
